import { useCallback, useMemo, useState } from "react";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import {
  LessThanDate,
  checkTwilioMessagingSurgeHit,
  deepCopy,
  detectedCensored,
  getEventAttachmentLinks,
  massTextMessageAttachmentsValidation,
  uniqueVals,
  MARKITAI,
  textTimeWithinQuietHours,
  hasSubscription,
  ONE_DAY_IN_MS,
  isDelayedTriggerAutomation,
  hostOnlyEventsAndLinks,
} from "@markit/common.utils";
import {
  MassText,
  MassTextType,
  SubSelectionItem,
  NotificationType,
  ScheduleTextInfo,
  Campaign,
  FREE_TIER_MESSAGE_LIMIT,
  DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
} from "@markit/common.types";
import { API } from "../../../API";
import { useSelector, useDispatch } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { MassTextMessagingScreenType } from "./MassTextPanel";
import mixpanel from "mixpanel-browser";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import HelpModal from "../../Containers/HelpModal";
import TestTextButton from "./TestTextButton";
import { getEventState } from "../../../redux/slices/eventSlice";
import AlertContainer from "../../Containers/AlertContainer";
import { saveMediaToFirestore } from "../../../utils/photoUtils";
import ContentViolationAlert from "../../DeliveryScore/ContentViolationAlert";
import { useLocation, useNavigate } from "react-router-dom";
import { onCampaignNavigatePath } from "../../../utils/navigationUtils";
import { NavigationId } from "../../../navigation/AppParamList";
import { MassTextConfirmDeleteModal } from "./Items/MassTextConfirmDeleteModal";
import { getCampaignState } from "../../../redux/slices/campaignSlice";
import { MassTextsWrapperType } from "./MassTextWrapperManager";
import MassTextsHeaderPreview from "./Header/MassTextsHeaderPreview";
import RectangleButton from "../../Buttons/RectangleButton";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import QuietHoursSendingModal from "./QuietHours/QuietHoursSendingModal";
import { ConfirmSendTextModal } from "./Message/ConfirmSendTextModal";
import { BackButton } from "../../Buttons/BackButton";
import { MessageLimitsModal } from "./Message/MessageLimitsModal";
import { checkIfTriggerTextOffsetExists } from "../../../utils/campaignUtils";
import {
  getRecentlySentMassText,
  checkValidTextMessage,
} from "../../../utils/textingUtils";

type MassTextsMessagingHeaderProps = {
  wrapperType: MassTextsWrapperType;
  campaign: Campaign | undefined;
  setEditingCampaign?: (editingCampaign: boolean) => void;
  duplicateOnPress?: (massText: MassText) => void;
  screen: MassTextMessagingScreenType;
  setScreen: (screen: MassTextMessagingScreenType) => void;
  massText: MassText;
  updateMassTextSettings: (massTextSettings: Partial<MassText>) => void;
  numCredits: number;
  numCreditsOppositeCompliance: number;
  numFollowersNotCompliant: number;
  showComplianceCreditNotice: boolean;
  resetState: (newMassText?: MassText) => void;
  madeChanges: boolean;
  finalRecipients: string[];
  suggestedAction: SubSelectionItem | undefined;
  scheduleTextInfo: ScheduleTextInfo;
  setScheduleTextInfo: (ScheduleTextInfo: ScheduleTextInfo) => void;
  isEditing: boolean;
  totalDailyMessages: number;
  loadingDailyMessages: boolean;
  loadingCategoryRecipients: boolean;
};

const MassTextsMessagingHeader = (props: MassTextsMessagingHeaderProps) => {
  const {
    wrapperType,
    campaign,
    setEditingCampaign,
    duplicateOnPress,
    screen,
    setScreen,
    massText,
    updateMassTextSettings,
    numCredits,
    numCreditsOppositeCompliance,
    numFollowersNotCompliant,
    showComplianceCreditNotice,
    resetState,
    madeChanges,
    finalRecipients,
    suggestedAction,
    scheduleTextInfo,
    setScheduleTextInfo,
    isEditing,
    totalDailyMessages,
    loadingDailyMessages,
    loadingCategoryRecipients,
  } = props;
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const { accountData } = useSelector(getAccountState).account;
  const { campaignDetails } = useSelector(getCampaignState);
  const { events } = useSelector(getEventState).events;
  const dispatch = useDispatch();
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [quietHoursModal, setQuietHoursModal] = useState(false);
  const [sendTextConfirm, setSendTextConfirm] = useState(false);
  const [messageLimitsModal, setMessageLimitsModal] = useState(false);
  const [showConfirmBackModal, setShowConfirmBackModal] =
    useState<boolean>(false);
  const [showDeleteMassTextModal, setShowDeleteMassTextModal] =
    useState<boolean>(false);

  const [initialMassText, setInitialMassText] = useState(deepCopy(massText));
  const [alertText, setAlertText] = useState<{
    heading: string;
    subHeading: string;
    closeModal?: boolean;
  }>({
    heading: "",
    subHeading: "",
    closeModal: false,
  });
  const [messageContentAlert, setMessageContentAlert] = useState(false);
  const [sentRecentText, setSentRecentText] = useState(false);
  const [sending, setSending] = useState(false);
  const [isImmediateSend, setIsImmediateSend] = useState(false); // state for if sending immediate text from within dashboard

  // Use this instead of scheduleTextInfo to handle cases when not on send schedule screen
  const textInfo: ScheduleTextInfo = useMemo(() => {
    if (screen === MassTextMessagingScreenType.SEND_SCHEDULE) {
      return scheduleTextInfo;
    } else {
      return {
        sentAt: massText.sentAt,
        scheduleToggle: massText.scheduled,
        catchUpChecked: massText.catchUpEnabled,
        sentAtOffset: massText.sentAtOffset,
      };
    }
  }, [
    massText.catchUpEnabled,
    massText.scheduled,
    massText.sentAt,
    massText.sentAtOffset,
    scheduleTextInfo,
    screen,
  ]);

  // The event that the event text is for
  const foundEventText = useMemo(
    () => events.find((event) => event.id === massText.eventRefId),
    [events, massText.eventRefId]
  );

  // The initial text in the campaign, if this text is part of an existing campaign
  const initialCampaignText = useMemo(() => {
    const foundCampaignDetail = campaignDetails.find(
      (detail) => detail.campaignId === massText.campaignId
    );
    if (foundCampaignDetail && campaign) {
      const foundInitialText = foundCampaignDetail.massTextDetails.find(
        (text) => text.massText.id === campaign.initialTextId
      );
      return foundInitialText?.massText;
    }
  }, [campaignDetails, campaign, massText.campaignId]);

  const isTriggerText = useMemo(
    () => massText.type === MassTextType.TRIGGERTEXT,
    [massText.type]
  );

  const finalSubSelectionIds = useMemo(
    () => (suggestedAction ? [suggestedAction] : massText.subSelectionIds),
    [massText.subSelectionIds, suggestedAction]
  );

  const isSendTimePassed = useMemo(
    () =>
      (LessThanDate(massText.sentAt, new Date().toISOString()) &&
        !isTriggerText) ||
      isImmediateSend,
    [isTriggerText, massText.sentAt, isImmediateSend]
  );

  const handleBackNavigation = useCallback(() => {
    switch (screen) {
      case MassTextMessagingScreenType.LINK_ANALYTICS:
        setScreen(MassTextMessagingScreenType.SEND_SUMMARY);
        break;
      case MassTextMessagingScreenType.SUMMARY:
      case MassTextMessagingScreenType.SEND_SUMMARY:
        resetState();
        break;
      case MassTextMessagingScreenType.RECIPIENTS_SEND_SUMMARY:
        setScreen(MassTextMessagingScreenType.SEND_SUMMARY);
        break;
      case MassTextMessagingScreenType.SEND_SCHEDULE:
        setScheduleTextInfo({
          scheduleToggle: massText.scheduled,
          sentAt: massText.sentAt,
          catchUpChecked: massText.catchUpEnabled,
          sentAtOffset: massText.sentAtOffset,
        });
        setScreen(
          isEditing
            ? MassTextMessagingScreenType.SEND_SUMMARY
            : MassTextMessagingScreenType.SUMMARY
        );
        break;
    }
  }, [
    isEditing,
    massText.catchUpEnabled,
    massText.scheduled,
    massText.sentAt,
    massText.sentAtOffset,
    resetState,
    screen,
    setScheduleTextInfo,
    setScreen,
  ]);

  const backOnPress = useCallback(() => {
    if (
      !madeChanges ||
      screen === MassTextMessagingScreenType.RECIPIENTS_SEND_SUMMARY
    ) {
      handleBackNavigation();
    } else {
      setShowConfirmBackModal(true);
    }
  }, [madeChanges, screen, handleBackNavigation]);

  const headerLabel = useMemo(() => {
    if (screen === MassTextMessagingScreenType.SEND_SUMMARY) {
      return isTriggerText && campaign
        ? "Auto Text"
        : isSendTimePassed
          ? "Text Performance"
          : "Scheduled Text";
    } else if (
      screen === MassTextMessagingScreenType.SUMMARY &&
      wrapperType !== MassTextsWrapperType.MODAL
    ) {
      return "New Text";
    } else if (screen === MassTextMessagingScreenType.RECIPIENTS_SEND_SUMMARY) {
      return "Text Recipients";
    } else if (screen === MassTextMessagingScreenType.LINK_ANALYTICS) {
      return "Link Analytics";
    } else if (screen === MassTextMessagingScreenType.SEND_SCHEDULE) {
      return isTriggerText ? "Time Delay" : "Sending Schedule";
    }
    return "";
  }, [campaign, isSendTimePassed, isTriggerText, screen, wrapperType]);

  const hideTopRightButton = useMemo(() => {
    return (
      screen === MassTextMessagingScreenType.RECIPIENTS_SEND_SUMMARY ||
      screen === MassTextMessagingScreenType.LINK_ANALYTICS ||
      (screen === MassTextMessagingScreenType.SEND_SUMMARY && isSendTimePassed)
    );
  }, [isSendTimePassed, screen]);

  const topRightButtonSave = useMemo(
    () =>
      screen === MassTextMessagingScreenType.SEND_SUMMARY ||
      massText.type === MassTextType.TRIGGERTEXT ||
      massText.campaignId === DEFAULT_EVENT_TEXTS_CAMPAIGN_ID,
    [massText.campaignId, massText.type, screen]
  );

  const eventAttachmentLinks = useMemo(() => {
    const allEventsAndLinks = hostOnlyEventsAndLinks(events, accountData.uid);
    const allEventIds = uniqueVals(
      massText.eventRefId
        ? allEventsAndLinks.map((event) => event.id).concat(massText.eventRefId)
        : allEventsAndLinks.map((event) => event.id)
    );
    return getEventAttachmentLinks(massText.message, allEventIds);
  }, [accountData.uid, events, massText.eventRefId, massText.message]);

  const sendingTextError = useCallback(
    (error: any) => {
      console.log("sendingTextError: ", error.message);
      setAlertText({
        heading: "An error occurred!!!",
        subHeading:
          "Please contact our hotline 617-213-0897 to diagnose the issue before attempting to send again.",
      });
      mixpanel.track("Mass Text Message Failed", {
        distinct_id: accountData.uid,
        message: massText.message,
        recipientsLength: finalRecipients.length,
        numExcludedRecipients: 0,
        errorMessage: error.message,
      });
    },
    [accountData.uid, massText.message, finalRecipients.length]
  );

  const saveChangesToText = useCallback(async () => {
    if (massText) {
      setSending(true);
      try {
        let finalUpdatedMassText = massText;
        if (
          massText.mediaUrl !== "" &&
          massText.mediaUrl !== initialMassText.mediaUrl
        ) {
          const url = await saveMediaToFirestore(
            massText.mediaUrl,
            accountData.uid + "/textMedia/massTexts/" + massText.id,
            accountData.customer.contactCardUrl
          );
          finalUpdatedMassText = { ...massText, mediaUrl: url };
        }
        await API.text
          .updateMassTextMessage({
            userId: accountData.uid,
            massText: finalUpdatedMassText,
          })
          .then((response) => {
            if (!response.success) {
              sendingTextError(
                new Error("An error occurred when updating the text")
              );
            } else {
              updateMassTextSettings(response.updatedMassText);
              showNotificationBanner(
                dispatch,
                "Text Updated!",
                NotificationType.AFFIRMATIVE
              );
              resetState();
            }
          });
      } catch (error) {
        sendingTextError(error);
      } finally {
        setSending(false);
      }
    }
  }, [
    massText,
    initialMassText.mediaUrl,
    accountData.uid,
    accountData.customer.contactCardUrl,
    sendingTextError,
    updateMassTextSettings,
    dispatch,
    resetState,
  ]);

  // Checks if the send time is valid
  const confirmSendTimeChecks = useCallback(async () => {
    if (massText.scheduled && isSendTimePassed) {
      setAlertText({
        heading: "Please edit the send time.",
        subHeading: "The scheduled send time is set before the current time.",
      });
      return false;
    }
    // If the send time is less than the initial mass text in the campaign (and not editing the initial text)
    // or if the initial mass text is not queued and trying to send a text now, prevent from sending
    if (
      initialCampaignText &&
      initialCampaignText.id !== massText.id &&
      (LessThanDate(massText.sentAt, initialCampaignText.sentAt) ||
        (!initialCampaignText.queued && !massText.scheduled))
    ) {
      setAlertText({
        heading: "Please edit the send time.",
        subHeading:
          "The send time cannot be prior to the first text in the campaign.",
      });
      return false;
    }

    // Surface as a banner in ConfirmSendTextModal (not a blocking alert) if the
    // creator sent a mass text within the last 15 minutes - never prevents sending.
    const recentMassText = await getRecentlySentMassText(accountData.uid);
    setSentRecentText(!massText.scheduled ? !!recentMassText : false);
    return true;
  }, [
    accountData.uid,
    initialCampaignText,
    isSendTimePassed,
    massText.id,
    massText.scheduled,
    massText.sentAt,
  ]);

  const sendMassText = useCallback(async () => {
    const finalMessage = massTextMessageAttachmentsValidation(
      massText.message,
      eventAttachmentLinks,
      finalRecipients.length
    );
    setSending(true);

    try {
      // upload media if it exists
      let mediaUrl = "";
      if (massText.mediaUrl !== "") {
        const url = await saveMediaToFirestore(
          massText.mediaUrl,
          accountData.uid + "/textMedia/massTexts/" + massText.id,
          accountData.customer.contactCardUrl
        );
        mediaUrl = url;
      }

      await API.text
        .sendCustomMessage({
          userData: accountData,
          message: finalMessage,
          sendTime: massText.scheduled ? massText.sentAt : "",
          sentAtOffset: massText.sentAtOffset,
          catchUpEnabled: massText.catchUpEnabled,
          eventRefId: massText.eventRefId,
          subSelectionIds: finalSubSelectionIds,
          excludedSubSelectionIds: massText.excludedSubSelectionIds,
          recipients: finalRecipients,
          numExcludedRecipients: 0,
          mediaUrl: mediaUrl,
          campaignId: massText.campaignId ?? "",
          massTextId: massText.id,
          type: massText.type,
          sendToAllFollowers: false,
        })
        .then((response) => {
          if (!response.success) {
            sendingTextError(new Error("response.success returned false"));
            return;
          }

          // Throw an alert to the user if the text was already sent prior (duplicate).
          if (response.alreadySent) {
            setAlertText({
              heading: "This text was already sent!",
              subHeading:
                "Looks like an earlier attempt to send this actually went through - no need to send it again. Check out your recent texts to view the text.",
              closeModal: false,
            });
            setSendTextConfirm(false);
            return;
          }

          if (mixpanel) {
            mixpanel.track("Mass Text Message Initialized", {
              source: "webapp",
              type: response.massText.type,
              massTextId: response.massText.id,
            });
          }
          updateMassTextSettings({
            id: response.massText.id,
            campaignId: response.massText.campaignId,
            sentAt: response.massText.sentAt,
            type: response.massText.type,
          });

          showNotificationBanner(
            dispatch,
            isTriggerText
              ? "Text Saved!"
              : massText.scheduled
                ? "Text Scheduled!"
                : "Text Sent!",
            NotificationType.AFFIRMATIVE
          );
          if (wrapperType !== MassTextsWrapperType.MODAL) {
            if (!response.massText.scheduled) {
              setIsImmediateSend(true);
            }
            resetState(response.massText);
            setScreen(MassTextMessagingScreenType.SEND_SUMMARY);
          } else {
            // Do a window navigate if modal panel and already on a campaign path (ie. duplicate text)
            if (pathname.startsWith("/c")) {
              window.location.href = `${MARKITAI}${onCampaignNavigatePath(
                response.massText.campaignId
              )}`;
            } else {
              navigate(onCampaignNavigatePath(response.massText.campaignId), {
                state: { campaignId: response.massText.campaignId },
              });
            }
          }
        })
        .catch((e: any) => {
          sendingTextError(e);
        });
    } catch (e: any) {
      sendingTextError(e);
    } finally {
      setSending(false);
    }
  }, [
    massText.message,
    massText.mediaUrl,
    massText.scheduled,
    massText.sentAt,
    massText.sentAtOffset,
    massText.catchUpEnabled,
    massText.eventRefId,
    massText.excludedSubSelectionIds,
    massText.campaignId,
    massText.id,
    massText.type,
    eventAttachmentLinks,
    finalRecipients,
    accountData,
    finalSubSelectionIds,
    updateMassTextSettings,
    dispatch,
    isTriggerText,
    wrapperType,
    sendingTextError,
    resetState,
    setScreen,
    pathname,
    navigate,
  ]);

  const confirmMassText = useCallback(async () => {
    const { valid, alertHeading, alertSubHeading } = checkValidTextMessage(
      massText.message,
      numCredits
    );
    if (!valid) {
      setAlertText({ heading: alertHeading, subHeading: alertSubHeading });
      return;
    }
    // if banned terms exist in message
    if (detectedCensored(massText.message)) {
      setMessageContentAlert(true);
      return;
    }
    if (!isTriggerText) {
      // if an event text and not scheduled and there are no attendees for the event
      if (
        finalRecipients.length === 0 &&
        massText.eventRefId &&
        !massText.scheduled
      ) {
        setAlertText({
          heading: "This message cannot be sent immediately.",
          subHeading:
            "You have no recipients within the custom selection. You can schedule this text instead.",
        });
        return;
      }
      // if there are no recipients selected for a non-category text
      if (finalRecipients.length === 0 && finalSubSelectionIds.length === 0) {
        setAlertText({
          heading: "Select recipients first!",
          subHeading:
            "Select recipients and write your message before sending.",
        });
        return;
      }

      // Check if sending total num credits for free tier user will reach their free credit limit
      if (
        !hasSubscription(accountData) &&
        accountData.customer.freeTierMessages +
          finalRecipients.length * numCredits >
          FREE_TIER_MESSAGE_LIMIT
      ) {
        setMessageLimitsModal(true);
        return false;
      }
      // If trying to send/schedule during quiet hours
      if (textTimeWithinQuietHours(textInfo) && !accountData.bypassQuietHours) {
        setQuietHoursModal(true);
        return;
      }

      if (!(await confirmSendTimeChecks())) {
        return;
      }
      // loading state for fetching the recipients from the categories
      if (loadingCategoryRecipients) {
        setAlertText({
          heading: "Not ready to send.",
          subHeading:
            "Currently fetching the recipients this text will be sent to. This will only take a few seconds.",
        });
        return;
      }
      // if the daily message count is loading, there are more than 1000 recipients selected, and the user is not a subaccount
      if (
        loadingDailyMessages &&
        finalRecipients.length > 100 &&
        !accountData.customer.subAccountSid
      ) {
        setAlertText({
          heading: "Not ready to send.",
          subHeading:
            "We are making sure everything is all set. This will only take up to a minute.",
        });
        return;
      }
      // If there are more than 100 recipients selected, the messaging surge is hit, and the user is not a subaccount
      if (
        finalRecipients.length > 100 &&
        checkTwilioMessagingSurgeHit(
          totalDailyMessages + finalRecipients.length,
          accountData.customer.subAccountSid
        )
      ) {
        mixpanel.track("Mass Texting Surge Modal Visible", {
          totalTwilioMessages: totalDailyMessages,
          numRecipients: finalRecipients.length,
          text_type: "mass text",
          from: "web-app",
        });
        setAlertText({
          heading: "We are currently experiencing a surge in Mass Texting.",
          subHeading:
            "To ensure critical texts are being sent [tickets, confirmations, etc], we have locked this feature until 12AM PST. Scheduled texts and Event texts will still be sent. Thank you for your patience.",
        });
        return;
      }
    } else {
      if (campaign && isDelayedTriggerAutomation(campaign, foundEventText)) {
        // Check if offset is the same as an existing trigger text
        const offsetExists = await checkIfTriggerTextOffsetExists(
          accountData.uid,
          massText.campaignId,
          massText.sentAtOffset,
          massText.id
        );
        if (offsetExists) {
          setAlertText({
            heading: "Please choose a different time delay.",
            subHeading:
              "Another auto text already exists with this time delay. You cannot create two texts with the same delay.",
          });
          return;
        }
      }
    }
    if (screen === MassTextMessagingScreenType.SEND_SUMMARY) {
      if (isTriggerText) {
        setSendTextConfirm(true);
      } else {
        saveChangesToText();
      }
    } else {
      setSendTextConfirm(true);
    }
  }, [
    massText.message,
    massText.eventRefId,
    massText.scheduled,
    massText.campaignId,
    massText.sentAtOffset,
    massText.id,
    numCredits,
    isTriggerText,
    screen,
    finalRecipients.length,
    finalSubSelectionIds.length,
    accountData,
    textInfo,
    confirmSendTimeChecks,
    loadingCategoryRecipients,
    loadingDailyMessages,
    totalDailyMessages,
    campaign,
    foundEventText,
    saveChangesToText,
  ]);

  const saveSendTimeOnPress = useCallback(() => {
    if (
      Math.abs(textInfo.sentAtOffset) / ONE_DAY_IN_MS < 0 ||
      Math.abs(textInfo.sentAtOffset) / ONE_DAY_IN_MS > 365
    ) {
      setAlertText({
        heading: "Invalid time offset days.",
        subHeading: "Please choose between 0 and 365 days.",
      });
      return;
    }
    if (textTimeWithinQuietHours(textInfo) && !accountData.bypassQuietHours) {
      setQuietHoursModal(true);
      return;
    }
    updateMassTextSettings({
      scheduled: textInfo.scheduleToggle,
      sentAt: textInfo.sentAt,
      catchUpEnabled: textInfo.catchUpChecked,
      sentAtOffset: textInfo.sentAtOffset,
    });
    setScreen(
      isEditing
        ? MassTextMessagingScreenType.SEND_SUMMARY
        : MassTextMessagingScreenType.SUMMARY
    );
  }, [
    textInfo,
    accountData.bypassQuietHours,
    updateMassTextSettings,
    setScreen,
    isEditing,
  ]);

  const handleTopRightButton = useCallback(async () => {
    if (
      screen === MassTextMessagingScreenType.SUMMARY ||
      screen === MassTextMessagingScreenType.SEND_SUMMARY
    ) {
      confirmMassText();
    } else if (screen === MassTextMessagingScreenType.SEND_SCHEDULE) {
      saveSendTimeOnPress();
    }
  }, [confirmMassText, saveSendTimeOnPress, screen]);

  if (wrapperType === MassTextsWrapperType.SIDE_PANEL_PREVIEW) {
    return (
      <MassTextsHeaderPreview
        screen={screen}
        setEditingCampaign={setEditingCampaign}
        duplicateOnPress={duplicateOnPress}
        massText={massText}
        headerLabel={headerLabel}
        handleBackNavigation={handleBackNavigation}
      />
    );
  }

  return (
    <>
      <div
        className="AlignedRowSpaced"
        style={{ height: 56, paddingInline: 14 }}
      >
        <div className="AlignedRow" style={{ gap: 20 }}>
          <BackButton
            onPress={backOnPress}
            iconName={
              screen === MassTextMessagingScreenType.SUMMARY ||
              screen === MassTextMessagingScreenType.SEND_SUMMARY
                ? "mdi:close"
                : "ion:chevron-back"
            }
          />
          <span className="sectionTitle">{headerLabel}</span>
        </div>
        <div className="AlignedRow" style={{ gap: 7 }}>
          {screen === MassTextMessagingScreenType.SEND_SUMMARY &&
          !isSendTimePassed &&
          !isTriggerText ? (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => setShowDeleteMassTextModal(true)}
            >
              <Icon
                icon="ion:trash"
                height={24}
                style={{ color: Colors.RED1 }}
              />
            </div>
          ) : screen === MassTextMessagingScreenType.SUMMARY ? (
            <div
              onClick={() => setShowHelpModal(true)}
              className="Centering"
              style={{
                width: 36,
                height: 36,
                border: `0.5px solid ${Colors.GRAY2}`,
                borderRadius: 100,
                backgroundColor: Colors.WHITE1,
                cursor: "pointer",
              }}
            >
              <Icon icon="ion:help" height={25} />
            </div>
          ) : null}
          {!isSendTimePassed &&
          (screen === MassTextMessagingScreenType.SUMMARY ||
            screen === MassTextMessagingScreenType.SEND_SUMMARY) ? (
            <TestTextButton
              massText={massText}
              image={massText.mediaUrl}
              save={massText.mediaUrl !== initialMassText.mediaUrl}
              event={foundEventText}
            />
          ) : null}
          {!hideTopRightButton ? (
            <div>
              <RectangleButton
                buttonLabel={
                  topRightButtonSave
                    ? "Save"
                    : screen === MassTextMessagingScreenType.SUMMARY
                      ? massText.scheduled
                        ? "Schedule"
                        : "Send"
                      : "Done"
                }
                onPress={handleTopRightButton}
                altBorderRadius={100}
                altPaddingHorz={14}
                altPaddingVert={10}
                width={sending ? 60 : undefined}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                loading={sending}
                disabled={
                  (screen === MassTextMessagingScreenType.SEND_SCHEDULE ||
                    screen === MassTextMessagingScreenType.SEND_SUMMARY) &&
                  !madeChanges
                }
                iconRight={
                  !topRightButtonSave &&
                  screen !== MassTextMessagingScreenType.SEND_SCHEDULE ? (
                    <Icon
                      icon={
                        massText.scheduled ? "ion:timer" : "ion:paper-plane"
                      }
                      height={16}
                      style={{ color: Colors.WHITE }}
                    />
                  ) : undefined
                }
              />
            </div>
          ) : null}
        </div>
      </div>
      {showDeleteMassTextModal ? (
        <MassTextConfirmDeleteModal
          massText={massText}
          campaignInitialText={initialCampaignText}
          isOnlyMassText={false}
          showDeleteModal={showDeleteMassTextModal}
          setShowDeleteModal={setShowDeleteMassTextModal}
          handleAfterDelete={() => {
            if (!initialCampaignText) {
              alert("Please refresh the page to delete.");
            } else {
              if (initialCampaignText.id === massText.id) {
                navigate(NavigationId.HOME_TEXTING);
              } else {
                resetState();
              }
            }
          }}
        />
      ) : null}
      <HelpModal showModal={showHelpModal} setShowModal={setShowHelpModal} />
      {quietHoursModal ? (
        <QuietHoursSendingModal
          scheduleTextInfo={textInfo}
          setScheduleTextInfo={(scheduleTextInfo: ScheduleTextInfo) => {
            setScheduleTextInfo(scheduleTextInfo);
            updateMassTextSettings({
              sentAt: scheduleTextInfo.sentAt,
              scheduled: scheduleTextInfo.scheduleToggle,
            });
          }}
          setIsVisible={setQuietHoursModal}
        />
      ) : null}
      <ConfirmDeleteModal
        heading="Are you sure you want to exit?"
        subtext="Your edits won't be saved. This cannot be undone."
        deleteButtonText="Exit"
        hideModal={!showConfirmBackModal}
        setIsVisible={setShowConfirmBackModal}
        deleteOnPress={() => {
          setShowConfirmBackModal(false);
          if (screen === MassTextMessagingScreenType.SUMMARY) {
            resetState();
          } else {
            handleBackNavigation();
          }
        }}
        icon={<Icon icon="ion:exit-outline" height={40} />}
      />
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => {
          setAlertText({ heading: "", subHeading: "" });
          if (alertText.closeModal) {
            resetState();
          }
        }}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
      <ContentViolationAlert
        message={massText.message}
        isVisible={messageContentAlert}
        setIsVisible={setMessageContentAlert}
      />
      {sendTextConfirm && !alertText.heading ? (
        <ConfirmSendTextModal
          setModalVisible={setSendTextConfirm}
          confirmOnPress={
            screen === MassTextMessagingScreenType.SEND_SUMMARY
              ? saveChangesToText
              : sendMassText
          }
          numCredits={numCredits}
          numCreditsOppositeCompliance={numCreditsOppositeCompliance}
          numFollowersNotCompliant={numFollowersNotCompliant}
          showComplianceCreditNotice={showComplianceCreditNotice}
          isTriggerText={isTriggerText}
          isScheduled={textInfo.scheduleToggle}
          massText={massText}
          updateMassTextSettings={updateMassTextSettings}
          recipients={isTriggerText ? [] : finalRecipients}
          isSending={sending}
          showCategories={
            massText.subSelectionIds.length > 0 || suggestedAction !== undefined
          }
          sentRecentText={sentRecentText}
        />
      ) : null}
      {messageLimitsModal ? (
        <MessageLimitsModal
          setModalVisible={setMessageLimitsModal}
          numCredits={numCredits}
          numRecipients={finalRecipients.length}
          isScheduled={textInfo.scheduleToggle}
        />
      ) : null}
    </>
  );
};

export default MassTextsMessagingHeader;
